'use client'

/**
 * FglRaceTrack — wiederverwendbare Race-Track-Komponente.
 * r19.45 (Stephan-Spec 2026-06-26)
 *
 * Horizontaler Glas-Streifen mit Top-10-Piloten als Avatare proportional zur
 * Σ-km positioniert. Hover auf Avatar zeigt Sort-Card (km / Klasse) mit
 * klickbaren Flug-Pillen → /flight/[id]. Self-Fetching: lädt selbst die
 * Overall-Wertung pro Terrain.
 *
 * Einsatz:
 *   Frontpage: 2 Tracks (Flatland + Slope, beide immer sichtbar)
 *   /wertung Schnellüberblick: 1 Track (folgt dem aktuellen Terrain-State)
 */

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { DonorAvatar } from '@/components/ui/donor-avatar'
import { useTranslation } from '@/i18n/client'

/**
 * r19.49 (Stephan-Spec 2026-06-29): Deep-Link von der Race-Track zur
 * /wertung-Seite. View aus dem Frontpage-Tab (RaceTrackView) → URL-Param-
 * Mapping. Optional pilotId als Hash damit /wertung den entsprechenden
 * Eintrag scrollen + hervorheben kann.
 */
export function buildWertungLink(
  terrain: 'FLATLAND' | 'SLOPE',
  view: 'overall' | 'einzelflug' | 'CLUBCLASS' | 'STANDARD' | 'OPEN',
  pilotId?: string | null,
): string {
  const params = new URLSearchParams({
    terrain: terrain === 'SLOPE' ? 'slope' : 'flatland',
  })
  if (view === 'overall' || view === 'einzelflug') {
    params.set('view', view)
  } else {
    params.set('view', 'klasse')
    params.set('class', view)
  }
  const hash = pilotId ? `#pilot-${pilotId}` : ''
  return `/wertung?${params.toString()}${hash}`
}

interface ApiFlight {
  flightId: string
  date: string
  distanceKm: number
  avgKmh: number | null
  durationS: number | null
  classCode: 'CLUBCLASS' | 'STANDARD' | 'OPEN' | null
  reachedStages: string[]
  speedBatches: string[]
  terrainSubtype: string | null
}

interface ApiPilot {
  userId: string
  name: string
  slug: string | null
  avatarUrl: string | null
  donorTier: string | null
  countryCode: string | null
  clubName: string | null
}

interface ApiPilotRow {
  rank: number
  pilot: ApiPilot
  scoreKm: number
  flightCount: number
  flights: ApiFlight[]
  positionHeldSince: string | null
}

interface ApiEinzelflugEntry {
  rank: number
  pilot: ApiPilot
  flight: ApiFlight
}

interface ApiResponse {
  success: boolean
  data: {
    terrain: 'FLATLAND' | 'SLOPE'
    rows?: ApiPilotRow[]            // overall / klasse
    flights?: ApiEinzelflugEntry[]  // einzelflug
  }
}

type Terrain = 'FLATLAND' | 'SLOPE'
type Sort = 'km' | 'class'
export type RaceTrackView = 'overall' | 'einzelflug' | 'CLUBCLASS' | 'STANDARD' | 'OPEN'

interface Props {
  terrain: Terrain
  /** Oberhalb der Strecke angezeigtes Label (z.B. "Flatland · Top 10"). */
  label?: string
  /** Eingeloggter-User-Id für „Du bist hier"-Indikator. */
  highlightUserId?: string | null
  /** Live-Pulse-Dot oben links. */
  showLiveDot?: boolean
  /** Höhe des Streifens in px. Default 70 für Hero, 56 für /wertung. */
  height?: number
  /** Anzahl der Piloten die auf der Track gerendert werden. Default 10. */
  topN?: number
  /** Saison-Jahr — optional Override, default aktuelles Jahr. */
  season?: number
  /** r19.45.1: View — Overall / Einzelflug / Club / Standard / Open. Default 'overall'. */
  view?: RaceTrackView
}

const CLASS_ORDER: Record<string, number> = { CLUBCLASS: 0, STANDARD: 1, OPEN: 2 }
const TROPHIES = ['🏆', '🥈', '🥉']
const FLAG_BASE = 'https://flagcdn.com/'

function MiniFlag({ iso, size = 14 }: { iso: string | null | undefined; size?: number }) {
  const c = (iso || '').toLowerCase().trim()
  if (!/^[a-z]{2}$/.test(c)) return null
  return (
    <img
      src={`${FLAG_BASE}${c}.svg`}
      alt={c.toUpperCase()}
      width={size}
      height={Math.round(size * 0.67)}
      className="rounded-[2px] object-cover block"
      style={{ boxShadow: '0 0 0 1px rgba(0,0,0,.4), 0 2px 4px rgba(0,0,0,.3)' }}
      loading="lazy"
      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
    />
  )
}

function fmtKm(km: number): string {
  if (km >= 1000) return `${(km / 1000).toFixed(2)}k`
  return km.toFixed(0)
}

// r19.45.5: Flag-Größe je Rang — Top-1 grösser, Rest kleiner.
function p_rankFlagSize(rank: number): number {
  if (rank === 1) return 16
  if (rank === 2) return 14
  if (rank === 3) return 12
  return 10
}

export function FglRaceTrack({
  terrain,
  label,
  highlightUserId,
  showLiveDot,
  height = 70,
  topN = 10,
  season,
  view = 'overall',
}: Props) {
  const { t } = useTranslation()
  const [data, setData] = useState<ApiPilotRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hovered, setHovered] = useState<number | null>(null)
  const [sort, setSort] = useState<Sort>('km')
  // r19.49 (Stephan-Bug 2026-06-29): Grace-Period damit Maus die Lücke
  // zwischen Avatar und Hover-Card überqueren kann, ohne dass die Card
  // verschwindet. 180 ms hat sich als gut anfühlend bewährt.
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const showHover = (idx: number) => {
    if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null }
    setHovered(idx)
  }
  const scheduleHide = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current)
    hideTimer.current = setTimeout(() => setHovered(null), 180)
  }
  useEffect(() => {
    return () => { if (hideTimer.current) clearTimeout(hideTimer.current) }
  }, [])

  useEffect(() => {
    let abort = false
    setLoading(true)
    setError(null)
    const terrainParam = terrain === 'FLATLAND' ? 'flatland' : 'slope'
    const seasonStr = season ? `&season=${season}` : ''
    // r19.45.1: View-spezifische API-URL.
    // - overall → /api/public/wertung?view=overall
    // - einzelflug → /api/public/wertung?view=einzelflug  (Bester Flug)
    // - CLUBCLASS/STANDARD/OPEN → ?view=klasse&class=X
    let apiUrl: string
    if (view === 'overall') {
      apiUrl = `/api/public/wertung?view=overall&terrain=${terrainParam}${seasonStr}`
    } else if (view === 'einzelflug') {
      apiUrl = `/api/public/wertung?view=einzelflug&terrain=${terrainParam}${seasonStr}`
    } else {
      apiUrl = `/api/public/wertung?view=klasse&terrain=${terrainParam}&class=${view}${seasonStr}`
    }
    fetch(apiUrl, { cache: 'no-store' })
      .then(r => r.ok ? r.json() : r.json().then(j => Promise.reject(j?.error || `HTTP ${r.status}`)))
      .then((j: ApiResponse) => {
        if (abort) return
        if (!j.success || !j.data) {
          setError(t('raceTrack.noData'))
          return
        }
        // r19.45.2 (Stephan-Bug 2026-06-26): View 'einzelflug' liefert
        // flights[] mit { rank, pilot, flight } statt rows[] mit
        // { rank, pilot, scoreKm, flights[] }. Wir mappen das Format in
        // dasselbe Schema damit der Renderer eine einheitliche Quelle hat:
        // - scoreKm = flight.distanceKm (= Position auf der Track)
        // - flights = [flight]  (Hover-Card zeigt den einzelnen Flug)
        if (view === 'einzelflug' && j.data.flights) {
          const mapped: ApiPilotRow[] = j.data.flights.slice(0, topN).map(e => ({
            rank: e.rank,
            pilot: e.pilot,
            scoreKm: e.flight.distanceKm,
            flightCount: 1,
            flights: [e.flight],
            positionHeldSince: null,
          }))
          setData(mapped)
        } else if (j.data.rows) {
          setData(j.data.rows.slice(0, topN))
        } else {
          setError(t('raceTrack.noData'))
        }
      })
      .catch(e => { if (!abort) setError(typeof e === 'string' ? e : t('raceTrack.loadError')) })
      .finally(() => { if (!abort) setLoading(false) })
    return () => { abort = true }
  }, [terrain, season, topN, view])

  // Terrain-Akzent-Farbe + Strip-Gradient
  const terrainAccent = terrain === 'FLATLAND' ? '#22c55e' : 'var(--accent)'
  const stripBg = terrain === 'FLATLAND'
    ? 'linear-gradient(90deg, color-mix(in oklch, var(--bg-card) 70%, transparent) 0%, color-mix(in oklch, var(--bg-card) 92%, transparent) 50%, rgba(34,197,94,.22) 100%)'
    : 'linear-gradient(90deg, color-mix(in oklch, var(--bg-card) 70%, transparent) 0%, color-mix(in oklch, var(--bg-card) 92%, transparent) 50%, color-mix(in oklch, var(--accent) 22%, transparent) 100%)'

  // Max-km für proportionale Positionierung
  const maxKm = data && data.length > 0 ? data[0].scoreKm : 1
  const pilotX = (km: number) => 4 + (Math.min(km, maxKm) / maxKm) * 92

  if (loading) {
    return (
      <div className="relative w-full">
        {label && (
          <div className="text-[10px] font-black tracking-[.18em] uppercase mb-2 px-1" style={{ color: terrainAccent }}>
            {label}
          </div>
        )}
        <div
          className="relative rounded-full animate-pulse"
          style={{ height, background: stripBg, border: `1.5px solid color-mix(in oklch, ${terrainAccent} 30%, transparent)` }}
        />
      </div>
    )
  }

  if (error || !data || data.length === 0) {
    return (
      <div className="relative w-full">
        {label && (
          <div className="text-[10px] font-black tracking-[.18em] uppercase mb-2 px-1" style={{ color: terrainAccent }}>
            {label}
          </div>
        )}
        <div
          className="relative rounded-full flex items-center justify-center"
          style={{ height, background: stripBg, border: `1.5px solid color-mix(in oklch, ${terrainAccent} 30%, transparent)` }}
        >
          <span className="text-[10px] text-[var(--muted)] uppercase tracking-wider">
            {error || t('raceTrack.noFlights')}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="relative w-full">
      {/* Header */}
      {(label || showLiveDot) && (
        <div className="flex items-center gap-2 mb-2 px-1">
          {showLiveDot && (
            <div className="relative inline-flex items-center gap-1.5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
              </span>
              <span className="text-[10px] font-black tracking-[.2em] text-red-500">{t('raceTrack.live')}</span>
            </div>
          )}
          {label && (
            <span className="text-[10px] font-black tracking-[.18em] uppercase" style={{ color: terrainAccent }}>
              {label}
            </span>
          )}
          <div className="flex-1" />
          {/* r19.45.4 (Stephan-Bug 2026-06-26): Right-Side-Label klar nach View:
              - overall      → "Top N · Σ km Saison" (Summe Top-15-Flüge)
              - einzelflug   → "Top N · Beste Strecke" (Einzelflug-Rekord)
              - CL/ST/OP     → "Top N · Σ km Klassenwertung" (Top-5 in Klasse) */}
          <span className="text-[9px] text-[var(--muted)] tabular-nums">
            {t('raceTrack.topLabel', { count: data.length })} · {
              view === 'einzelflug' ? t('raceTrack.bestDistance') :
              view === 'overall'    ? t('raceTrack.seasonSumKm') :
                                      t('raceTrack.classSumKm')
            }
          </span>
        </div>
      )}

      {/* Strip */}
      <div
        className="relative rounded-full"
        style={{
          height,
          background: stripBg,
          border: `1.5px solid color-mix(in oklch, ${terrainAccent} 35%, var(--border))`,
        }}
      >
        {data.map((row, idx) => {
          const xPct = pilotX(row.scoreKm)
          const isYou = highlightUserId === row.pilot.userId
          const isTop3 = row.rank <= 3
          // r19.49 (2026-06-29, Stephan-Spec): Avatare in der Race-Track-Pille
          // sichtbar größer — vorher 36/32/28/22 wirkte verloren in der 70px-
          // hohen Glas-Pille. Jetzt 52/46/42/34 (~+45%) füllt die Pille besser.
          const avatarSize = row.rank === 1 ? 52 : row.rank === 2 ? 46 : row.rank === 3 ? 42 : 34

          // r19.49 (Stephan-Spec 2026-06-29): jeder Avatar ist ein Deep-Link
          // zur entsprechenden /wertung-Sicht (Terrain + View + Klasse) mit
          // Pilot-Anker im Hash.
          const wertungHref = buildWertungLink(terrain, view, row.pilot.userId)
          return (
            <Link
              key={row.pilot.userId}
              href={wertungHref}
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 cursor-pointer transition-transform hover:scale-110"
              style={{ left: `${xPct}%`, zIndex: isTop3 || isYou ? 5 : 1 }}
              onMouseEnter={() => showHover(idx)}
              onMouseLeave={scheduleHide}
              aria-label={t('raceTrack.avatarAriaLabel', { name: row.pilot.name })}
            >
              {/* Rang-Trophäe für Top-3 — ÜBER dem Avatar */}
              {isTop3 && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 text-[10px] font-black whitespace-nowrap pointer-events-none"
                  style={{
                    bottom: avatarSize + 4,
                    color: row.rank === 1 ? '#f2c94e' : row.rank === 2 ? '#cfd6e0' : '#cd7f32',
                  }}
                >
                  {TROPHIES[row.rank - 1]}
                </div>
              )}

              {/* „Du bist hier" — UNTER dem Avatar */}
              {isYou && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 text-[8px] font-black whitespace-nowrap pointer-events-none"
                  style={{ top: avatarSize + 4, color: '#f2c94e' }}
                >
                  {t('raceTrack.youIndicator', { rank: row.rank })}
                </div>
              )}

              {/* Glow für Gold + Du */}
              {(row.rank === 1 || isYou) && (
                <div
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none"
                  style={{
                    width: avatarSize * 1.8,
                    height: avatarSize * 1.8,
                    background: `radial-gradient(circle, color-mix(in oklch, var(--accent) 60%, transparent) 0%, transparent 70%)`,
                  }}
                />
              )}

              <DonorAvatar
                src={row.pilot.avatarUrl}
                name={row.pilot.name}
                donorTier={row.pilot.donorTier}
                size={avatarSize}
                showBadge={false}
              />

              {/* r19.45.5 (Stephan-Spec 2026-06-26): Nationalflagge unten
                  rechts als kleines Overlay. Top-3 grösser, Rest kleiner. */}
              {row.pilot.countryCode && (
                <div
                  className="absolute pointer-events-none"
                  style={{
                    bottom: -2,
                    right: -2,
                  }}
                >
                  <MiniFlag iso={row.pilot.countryCode} size={p_rankFlagSize(row.rank)} />
                </div>
              )}
            </Link>
          )
        })}

        {/* Skala-Marker */}
        <div
          className="absolute -bottom-5 left-[4%] right-[4%] flex justify-between text-[9px] tabular-nums pointer-events-none"
          style={{ color: 'var(--muted)' }}
        >
          <span>{t('raceTrack.scaleMinKm')}</span>
          <span className="font-black" style={{ color: terrainAccent }}>
            {fmtKm(maxKm)} km
          </span>
        </div>
      </div>

      {/* Hover-Card */}
      {hovered !== null && data[hovered] && data[hovered].flights.length > 0 && (
        <HoverCard
          row={data[hovered]}
          xPct={pilotX(data[hovered].scoreKm)}
          sort={sort}
          setSort={setSort}
          terrainAccent={terrainAccent}
          view={view}
          onMouseEnter={() => showHover(hovered)}
          onMouseLeave={scheduleHide}
        />
      )}
    </div>
  )
}

function HoverCard({
  row, xPct, sort, setSort, terrainAccent, view, onMouseEnter, onMouseLeave,
}: {
  row: ApiPilotRow
  xPct: number
  sort: Sort
  setSort: (s: Sort) => void
  terrainAccent: string
  view: RaceTrackView
  onMouseEnter: () => void
  onMouseLeave: () => void
}) {
  const { t } = useTranslation()
  const flightsSorted = [...row.flights].sort((a, b) => {
    if (sort === 'class') {
      const ca = CLASS_ORDER[a.classCode ?? ''] ?? 9
      const cb = CLASS_ORDER[b.classCode ?? ''] ?? 9
      if (ca !== cb) return ca - cb
    }
    return b.distanceKm - a.distanceKm
  })

  // r19.49 (Stephan-Spec 2026-06-29): Anzahl Flüge in der Hover-Card
  //   - Overall              → bis zu 15 (= Saison-Top-15 die in Σ einfließen)
  //   - Klassenwertung       → bis zu 5 (= Klassen-Top-5)
  //   - Einzelflug           → genau 1
  const maxFlights = view === 'einzelflug' ? 1
                   : view === 'overall'    ? 15
                                           : 5

  return (
    <div
      // r19.45.1: Hover-Bridge — pt-3 (12 px transparente Pufferzone) damit
      // die Maus die Lücke zwischen Avatar und Card überqueren kann.
      // r19.49 (Stephan-Bug 2026-06-29): zIndex 200000 weil das alte 100001
      // nicht durch alle Stacking-Contexts ausreichte — Hero-Backdrop +
      // Lenis-Scroll-Wrapper überlagerten die Card teilweise.
      className="absolute pointer-events-auto pt-3"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        left: xPct < 50 ? `calc(${xPct}% - 8px)` : 'auto',
        right: xPct >= 50 ? `calc(${100 - xPct}% - 8px)` : 'auto',
        width: 340,
        top: '100%',
        zIndex: 200000,
      }}
    >
    <div
      className="rounded-2xl p-3 shadow-2xl"
      style={{
        // r19.45.6 (Stephan-Bug 2026-06-26): vollständig opak — vorher 92 %
        // bg-card mit 8 % Akzent → der „Offizielle FGL Wertung"-Banner
        // dahinter schimmerte durch. Jetzt 100 % var(--bg-card) ohne Mix.
        background: 'var(--bg-card)',
        border: `1.5px solid color-mix(in oklch, ${terrainAccent} 55%, var(--border))`,
        boxShadow: '0 28px 56px rgba(0,0,0,.55), 0 0 0 1px rgba(255,255,255,.05) inset, 0 0 32px color-mix(in oklch, var(--accent) 18%, transparent)',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          {row.pilot.slug ? (
            <Link
              href={`/pilot/${row.pilot.slug}`}
              className="text-[12px] font-black truncate text-[var(--text)] hover:text-[var(--accent)] transition-colors block"
              onClick={e => e.stopPropagation()}
            >
              {row.pilot.name}
            </Link>
          ) : (
            <div className="text-[12px] font-black truncate text-[var(--text)]">
              {row.pilot.name}
            </div>
          )}
          <div className="text-[9px] text-[var(--muted)] truncate">
            {row.pilot.clubName ? `${row.pilot.clubName} · ` : ''}
            {t('raceTrack.subtitle', { km: row.scoreKm.toFixed(0), rank: row.rank })}
          </div>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {(['km', 'class'] as const).map(s => (
            <button
              key={s}
              type="button"
              onClick={e => { e.stopPropagation(); setSort(s) }}
              data-state={sort === s ? 'active' : 'inactive'}
              className="wertung-tab-pill px-2 py-0.5 rounded-full text-[8.5px] font-black uppercase tracking-wider"
            >
              {s === 'km' ? t('raceTrack.sortByKm') : t('raceTrack.sortByClass')}
            </button>
          ))}
        </div>
      </div>

      {/* Flug-Pillen — Anzahl je nach View (15 Overall / 5 Klasse / 1 Einzelflug) */}
      <div className="space-y-1 max-h-[380px] overflow-y-auto pr-1">
        {flightsSorted.slice(0, maxFlights).map(f => (
          <Link
            key={f.flightId}
            href={`/flight/${f.flightId}`}
            className="flex items-center gap-1.5 px-1.5 py-1 rounded-md transition-colors hover:bg-[color-mix(in_oklch,var(--accent)_12%,transparent)]"
            onClick={e => e.stopPropagation()}
          >
            <span className="text-[10px] font-black tabular-nums w-14 shrink-0" style={{ color: terrainAccent }}>
              {f.distanceKm.toFixed(1)} km
            </span>
            <span className="text-[9px] tabular-nums text-[var(--muted)] w-12 shrink-0">
              {f.avgKmh != null ? `${f.avgKmh.toFixed(0)} km/h` : '—'}
            </span>
            {f.classCode && (
              <span
                className="text-[8px] font-black uppercase tracking-tight px-1 py-0.5 rounded shrink-0"
                style={{
                  background:
                    f.classCode === 'CLUBCLASS' ? 'rgba(242,201,78,.18)' :
                    f.classCode === 'STANDARD'  ? 'rgba(96,165,250,.18)' :
                    'rgba(168,85,247,.18)',
                  color:
                    f.classCode === 'CLUBCLASS' ? '#f2c94e' :
                    f.classCode === 'STANDARD'  ? '#60a5fa' :
                    '#c084fc',
                }}
              >
                {f.classCode === 'CLUBCLASS' ? 'CL' : f.classCode === 'STANDARD' ? 'ST' : 'OP'}
              </span>
            )}
            <span className="text-[8.5px] text-[var(--muted)] truncate flex-1">
              {new Date(f.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
            </span>
          </Link>
        ))}
      </div>
    </div>
    </div>
  )
}
